import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  CreateSoftwareTypeInput,
  ISoftwareType,
  UpdateSoftwareTypeInput,
} from "@/features/servers/schemas/servers";
import {
  createSoftwareType,
  deleteSoftwareType,
  ensureSoftwareTypesSeeded,
  listSoftwareTypes,
  updateSoftwareType,
} from "@/features/servers/services/softwareTypesService";

export const softwareTypeKeys = {
  all: ["softwareTypes"] as const,
  list: () => [...softwareTypeKeys.all, "list"] as const,
};

/**
 * Catálogo de tipos de software. Seed automático si la colección está vacía.
 */
export function useSoftwareTypes() {
  return useQuery<ISoftwareType[]>({
    queryKey: softwareTypeKeys.list(),
    queryFn: ensureSoftwareTypesSeeded,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateSoftwareType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSoftwareTypeInput) => createSoftwareType(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: softwareTypeKeys.all });
    },
  });
}

export function useUpdateSoftwareType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateSoftwareTypeInput;
    }) => updateSoftwareType(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: softwareTypeKeys.all });
    },
  });
}

export function useDeleteSoftwareType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSoftwareType(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: softwareTypeKeys.all });
    },
  });
}

/** Re-export por si se necesita listar sin seed. */
export { listSoftwareTypes };
